import { AlertTriangle, BookOpen, GraduationCap, Pencil, Plus, Trash2, X } from "lucide-react";
import { FormEvent, useState } from "react";
import { api } from "../api";
import type { ClassCategory, ClassSummary } from "../types";

type Props = {
  academicYear: string;
  classItem?: ClassSummary;
  defaultCategory?: ClassCategory;
  onClose: () => void;
  onSaved: (classItem: ClassSummary, created: boolean) => void;
  onDeleted: (classId: string) => void;
};

export default function ClassModal({ academicYear, classItem, defaultCategory, onClose, onSaved, onDeleted }: Props) {
  const [name, setName] = useState(classItem?.name || "");
  const [category, setCategory] = useState<ClassCategory>(classItem?.category || defaultCategory || "regular");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      const result = classItem
        ? await api.updateClass(classItem.id, name.trim(), category)
        : await api.createClass(name.trim(), academicYear, category);
      onSaved(result.class, !classItem);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Không thể lưu lớp học.");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!classItem) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.deleteClass(classItem.id);
      onDeleted(classItem.id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Không thể xóa lớp học.");
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="compact-modal" role="dialog" aria-modal="true" aria-labelledby="class-modal-title">
        <header>
          <div className="compact-modal__icon">{classItem ? <Pencil /> : <Plus />}</div>
          <div><span className="modal-kicker">NĂM HỌC {academicYear}</span><h2 id="class-modal-title">{classItem ? "Chỉnh sửa lớp" : "Thêm lớp mới"}</h2></div>
          <button className="compact-close" onClick={onClose} aria-label="Đóng"><X /></button>
        </header>
        <form onSubmit={submit}>
          <label htmlFor="class-name">Tên lớp</label>
          <input id="class-name" autoFocus maxLength={40} placeholder="Ví dụ: 11A1 hoặc Luyện thi 12" value={name} onChange={(event) => { setName(event.target.value); setConfirmDelete(false); }} />
          <small>Tên lớp có thể chỉnh sửa bất cứ lúc nào.</small>

          <label className="modal-field-label">Phân loại lớp</label>
          <div className="class-category-cards" role="radiogroup" aria-label="Phân loại lớp">
            <button
              type="button"
              role="radio"
              aria-checked={category === "regular"}
              className={`category-card ${category === "regular" ? "is-selected" : ""}`}
              onClick={() => setCategory("regular")}
            >
              <BookOpen className="category-card__icon" />
              <div className="category-card__body">
                <strong>Lớp chính khóa</strong>
                <span>Chương trình tiêu chuẩn của trường</span>
              </div>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={category === "extra"}
              className={`category-card ${category === "extra" ? "is-selected" : ""}`}
              onClick={() => setCategory("extra")}
            >
              <GraduationCap className="category-card__icon" />
              <div className="category-card__body">
                <strong>Lớp dạy thêm</strong>
                <span>Bồi dưỡng, phụ đạo hoặc ôn thi</span>
              </div>
            </button>
          </div>

          {error && <div className="compact-error"><AlertTriangle />{error}</div>}
          {confirmDelete && <div className="delete-warning"><AlertTriangle /><span>Xóa lớp sẽ xóa toàn bộ học sinh và điểm của lớp. Có thể khôi phục bằng Hoàn tác trong phiên đăng nhập. Bấm “Xác nhận xóa” để xác nhận.</span></div>}
          <footer>
            {classItem && <button type="button" className={`danger-button ${confirmDelete ? "is-confirming" : ""}`} onClick={() => void remove()} disabled={submitting}><Trash2 />{confirmDelete ? "Xác nhận xóa" : "Xóa lớp"}</button>}
            <span />
            <button type="button" className="btn-secondary" onClick={onClose}>Hủy</button>
            <button type="submit" className="btn-primary-small" disabled={!name.trim() || submitting}>{submitting ? <span className="spinner" /> : classItem ? "Lưu thay đổi" : "Tạo lớp"}</button>
          </footer>
        </form>
      </section>
    </div>
  );
}
